import React, { useState, useEffect } from 'react';
import taxonomyConfig from '../../constants/taxonomyConfig.json';
import { resetNonApplicableFields } from '../../rules/propertyFieldRules';
import sortSelectOptions from '../../utils/sortSelectOptions';

/**
 * TaxonomySelector - Sélecteur de taxonomie en cascade avec reset contrôlé
 * 
 * COMPORTEMENT :
 * 1. Cascade : operation → objet → profil → sous_profil → cible
 * 2. Reset automatique des enfants quand parent change
 * 3. Modal warning si des champs remplis vont être neutralisés
 * 4. Aucune logique métier en dur (tout via taxonomyConfig.json + propertyFieldRules.js)
 */

const TaxonomySelector = ({ 
  formData,         // Données complètes du formulaire
  taxonomy,         // { operation, objet, profil, sous_profil, cible }
  onChange          // Callback (newTaxonomy, newFormData) => void
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);
  const [fieldsToLose, setFieldsToLose] = useState([]);
  
  // Obtenir les options disponibles selon le contexte
  const getAvailableOptions = (level) => {
    const config = taxonomyConfig;
    
    switch (level) {
      case 'operation':
        // NB: ordre business voulu (Cession → Vente → Location…) — NON trié alpha
        return Object.keys(config.operations || {}).map(key => ({
          value: key,
          label: config.operations[key].label
        }));
      
      case 'objet':
        if (!taxonomy.operation) return [];
        const opData = config.operations?.[taxonomy.operation];
        if (!opData || !opData.objets) return [];
        // Convertir l'objet objets en array (masquer les objets marqués hidden:true)
        // + tri alphabétique FR, "Autre" en bas, placeholder en haut
        return sortSelectOptions(
          Object.keys(opData.objets)
            .filter(key => !opData.objets[key].hidden)
            .map(key => ({
              value: key,
              label: opData.objets[key].label
            }))
        );
      
      case 'profil':
        if (!taxonomy.operation || !taxonomy.objet) return [];
        const opData2 = config.operations?.[taxonomy.operation];
        const objData = opData2?.objets?.[taxonomy.objet];
        if (!objData || !objData.profils) return [];
        // Tri alphabétique FR
        return sortSelectOptions(
          Object.keys(objData.profils).map(key => ({
            value: key,
            label: objData.profils[key].label
          }))
        );
      
      case 'sous_profil':
        if (!taxonomy.operation || !taxonomy.objet || !taxonomy.profil) return [];
        const opData3 = config.operations?.[taxonomy.operation];
        const objData2 = opData3?.objets?.[taxonomy.objet];
        const profilData = objData2?.profils?.[taxonomy.profil];
        if (!profilData || !profilData.sous_profils) return [];
        // Sous-profil : sauf pour "licence" (ordinal III/IV → NON trié)
        const rawSousProfils = Object.keys(profilData.sous_profils).map(key => ({
          value: key,
          label: profilData.sous_profils[key]
        }));
        if (taxonomy.profil === 'licence') {
          return rawSousProfils; // Licence III → IV → Autre : ordre ordinal préservé
        }
        return sortSelectOptions(rawSousProfils);
      
      case 'cible':
        // Cible dépend de operation/objet selon les règles métier
        // Pour l'instant, options génériques
        return [
          { value: 'utilisateur', label: 'Utilisateur' },
          { value: 'investisseur', label: 'Investisseur' }
        ];
      
      default:
        return [];
    }
  };
  
  // Détecter les champs qui vont être perdus
  const detectFieldsToLose = (currentFormData, nextTaxonomy) => {
    const cleanedData = resetNonApplicableFields(currentFormData, nextTaxonomy);
    const lostFields = [];
    
    // Comparer avant/après pour détecter les pertes
    const checkObject = (obj1, obj2, path = '') => {
      if (!obj1 || typeof obj1 !== 'object') return;
      
      for (const key in obj1) {
        const val1 = obj1[key];
        const val2 = obj2?.[key];
        const currentPath = path ? `${path}.${key}` : key;
        
        // Si la valeur était remplie et devient null
        if (val1 != null && val1 !== '' && (val2 === null || val2 === '')) {
          // Ne pas compter les champs de taxonomie eux-mêmes
          if (!['operation', 'objet', 'profil', 'sous_profil', 'cible'].includes(key)) {
            lostFields.push({
              path: currentPath,
              oldValue: val1
            });
          }
        }
        
        // Récursion pour objets imbriqués
        if (typeof val1 === 'object' && val1 !== null && !Array.isArray(val1)) {
          checkObject(val1, val2, currentPath);
        }
      }
    };
    
    checkObject(currentFormData, cleanedData);
    return lostFields;
  };
  
  // Gérer le changement de taxonomie
  const handleTaxonomyChange = (level, value) => {
    // 1. Construire la nouvelle taxonomie avec reset cascade
    let nextTaxonomy = { ...taxonomy, [level]: value };
    
    // Reset des enfants selon le niveau modifié
    if (level === 'operation') {
      nextTaxonomy.objet = null;
      nextTaxonomy.profil = null;
      nextTaxonomy.sous_profil = null;
      nextTaxonomy.cible = null;
    } else if (level === 'objet') {
      nextTaxonomy.profil = null;
      nextTaxonomy.sous_profil = null;
      
      // ⚠️ LICENCE : Auto-set profil = 'licence' si objet = 'licence_exploitation'
      if (value === 'licence_exploitation' || value === 'licence') {
        nextTaxonomy.profil = 'licence';
      }
    } else if (level === 'profil') {
      nextTaxonomy.sous_profil = null;
    }
    
    // 2. Détecter les champs qui vont être perdus
    const lostFields = detectFieldsToLose(formData, nextTaxonomy);
    
    // 3. Si des champs vont être perdus → modal warning
    if (lostFields.length > 0) {
      setPendingChange({ taxonomy: nextTaxonomy, lostFields });
      setFieldsToLose(lostFields);
      setShowWarningModal(true);
    } else {
      // Pas de perte → appliquer directement
      applyTaxonomyChange(nextTaxonomy);
    }
  };
  
  // Appliquer le changement (après confirmation ou si pas de perte)
  const applyTaxonomyChange = (nextTaxonomy) => {
    // Reset des champs non applicables
    const cleanedFormData = resetNonApplicableFields(formData, nextTaxonomy);
    
    // Purger activite_autre si le nouveau sous_profil n'est plus "autre_*"
    const isAutre = nextTaxonomy.sous_profil && String(nextTaxonomy.sous_profil).toLowerCase().startsWith('autre');
    const nextActiviteAutre = isAutre ? (cleanedFormData.activite_autre || formData.activite_autre || null) : null;
    // Preserver activite_precise (display uniquement, indépendant de la taxonomie sauf si l'utilisateur veut le vider manuellement)
    const nextActivitePrecise = cleanedFormData.activite_precise || formData.activite_precise || null;
    
    // Mettre à jour la taxonomie dans formData
    const updatedFormData = {
      ...cleanedFormData,
      operation: nextTaxonomy.operation,
      objet: nextTaxonomy.objet,
      profil: nextTaxonomy.profil,
      sous_profil: nextTaxonomy.sous_profil,
      cible: nextTaxonomy.cible,
      activite_autre: nextActiviteAutre,
      activite_precise: nextActivitePrecise,
    };
    
    // Callback parent
    onChange(nextTaxonomy, updatedFormData);
  };
  
  // Confirmer le changement (modal)
  const handleConfirmChange = () => {
    if (pendingChange) {
      applyTaxonomyChange(pendingChange.taxonomy);
    }
    setShowWarningModal(false);
    setPendingChange(null);
    setFieldsToLose([]);
  };
  
  // Annuler le changement (modal)
  const handleCancelChange = () => {
    setShowWarningModal(false);
    setPendingChange(null);
    setFieldsToLose([]);
  };
  
  // Options disponibles pour chaque niveau
  const operationOptions = getAvailableOptions('operation');
  const objetOptions = getAvailableOptions('objet');
  const profilOptions = getAvailableOptions('profil');
  const sousProfilOptions = getAvailableOptions('sous_profil');
  const cibleOptions = getAvailableOptions('cible');
  
  // Détection licence d'exploitation
  const isLicence = taxonomy.objet === 'licence_exploitation' || taxonomy.objet === 'licence';
  
  return (
    <>
      {/* lot DK3 — une seule colonne sous 768 px : « Local commercial » était coupé */}
      {/* lot DK4 — « Commerce de détail » restait coupé : 260 px minimum par menu */}
      <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3">
        {/* Type de transaction */}
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-1.5">
            Type de transaction <span className="text-red-500">*</span>
          </label>
          <select
            value={taxonomy.operation || ''}
            onChange={(e) => handleTaxonomyChange('operation', e.target.value || null)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Sélectionner...</option>
            {operationOptions.map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </select>
        </div>
        
        {/* Type de bien */}
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-1.5">
            Type de bien <span className="text-red-500">*</span>
          </label>
          <select
            value={taxonomy.objet || ''}
            onChange={(e) => handleTaxonomyChange('objet', e.target.value || null)}
            disabled={!taxonomy.operation || objetOptions.length === 0}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
          >
            <option value="">Sélectionner...</option>
            {objetOptions.map(obj => (
              <option key={obj.value} value={obj.value}>{obj.label}</option>
            ))}
          </select>
        </div>
        
        {/* Catégorie - MASQUÉ pour les licences */}
        {!isLicence && (
          <div>
            <label className="block text-xs font-medium text-slate-900 mb-1.5">
              Catégorie <span className="text-red-500">*</span>
            </label>
            <select
              value={taxonomy.profil || ''}
              onChange={(e) => handleTaxonomyChange('profil', e.target.value || null)}
              disabled={!taxonomy.objet || profilOptions.length === 0}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
            >
              <option value="">Sélectionner...</option>
              {profilOptions.map(prof => (
                <option key={prof.value} value={prof.value}>{prof.label}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Activité précise / Type de licence */}
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-1.5">
            {isLicence ? 'Type de licence' : 'Activité précise'} <span className="text-red-500">*</span>
          </label>
          <select
            value={taxonomy.sous_profil || ''}
            onChange={(e) => handleTaxonomyChange('sous_profil', e.target.value || null)}
            disabled={!taxonomy.profil || sousProfilOptions.length === 0}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
          >
            <option value="">Sélectionner...</option>
            {sousProfilOptions.map(sp => (
              <option key={sp.value} value={sp.value}>{sp.label}</option>
            ))}
          </select>
        </div>
        
        {/* Cible */}
        <div>
          <label className="block text-xs font-medium text-slate-900 mb-1.5">
            Cible
          </label>
          <select
            value={taxonomy.cible || ''}
            onChange={(e) => handleTaxonomyChange('cible', e.target.value || null)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Sélectionner...</option>
            {cibleOptions.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Libellé d'affichage (activite_precise) — TOUJOURS visible.
          iter102 : trois modes selon la taxonomie :
            (a) profil `autre` (« Autre / toutes activités ») → optionnel « Activité pressentie »
                (locaux vides sans activité déterminée : droit au bail, murs libres, …).
            (b) sous_profil `autre_*` sous un profil réel (chr, retail, licence…) → OBLIGATOIRE
                (« Précisez l'activité » : l'activité existe mais n'est pas dans la liste).
            (c) sinon → libellé d'affichage optionnel (remplace le libellé standard). */}
      {(() => {
        const isProfilNeutre = taxonomy.profil === 'autre';
        const isAutreSp = taxonomy.sous_profil && String(taxonomy.sous_profil).toLowerCase().startsWith('autre');
        // Requis UNIQUEMENT quand autre_* sur un profil réel (mode b)
        const isRequired = isAutreSp && !isProfilNeutre;
        // On veut afficher un champ typé "activite" dès que la clé autre est en jeu (mode a ou b),
        // pour maintenir la double-écriture activite_precise + activite_autre côté data.
        const usesActiviteChannel = isAutreSp; // couvre les deux modes autre
        const currentValue = formData.activite_precise || (usesActiviteChannel ? (formData.activite_autre || '') : '');
        const wrapperCls = isRequired ? 'mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3' : 'mt-3';
        const label = isRequired
          ? "Précisez l'activité"
          : (isProfilNeutre ? "Activité pressentie" : "Libellé d'affichage");
        const placeholder = isProfilNeutre
          ? "Ex : Toutes activités, Tous commerces sauf restauration, Idéal tertiaire…"
          : (isRequired ? "Ex : Salon de thé, Agence immobilière…" : "Ex : Brasserie coffee shop");
        const helperText = isProfilNeutre
          ? "Optionnel — décrit le potentiel du local sans le limiter à une activité fixe. S'affiche en priorité (fiche, PDF, listings)."
          : (!isRequired ? "Ce libellé remplace l'activité affichée (fiche publique, PDF, listings). Les statistiques restent basées sur la catégorie sélectionnée." : null);
        return (
          <div className={wrapperCls}>
            <label className="block text-xs font-medium text-slate-900 mb-1.5">
              {label}
              {isRequired && <span className="text-red-500"> *</span>}
              {!isRequired && <span className="text-slate-500 font-normal ml-1">(optionnel)</span>}
            </label>
            <input
              type="text"
              data-testid="taxonomy-activite-precise-input"
              value={currentValue}
              onChange={(e) => {
                const v = e.target.value;
                if (usesActiviteChannel) {
                  onChange(taxonomy, { ...formData, activite_precise: v, activite_autre: v });
                } else {
                  onChange(taxonomy, { ...formData, activite_precise: v });
                }
              }}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
            />
            {helperText && (
              <p className="text-[10px] text-slate-500 mt-1">{helperText}</p>
            )}
          </div>
        );
      })()}
      
      {/* Modal Warning */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-exclamation-triangle text-yellow-600 text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    Changement de taxonomie
                  </h3>
                  <p className="text-sm text-slate-700 mb-3">
                    Ce changement va <strong>réinitialiser {fieldsToLose.length} champ(s)</strong> qui ne sont pas compatibles avec la nouvelle taxonomie.
                  </p>
                  
                  {/* Liste des champs concernés (max 10 affichés) */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Champs qui seront réinitialisés :</p>
                    <ul className="space-y-1 text-xs text-slate-600">
                      {fieldsToLose.slice(0, 10).map((field, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <i className="bi bi-x-circle text-red-500 mt-0.5"></i>
                          <span>
                            <span className="font-mono text-[10px] bg-slate-200 px-1 rounded">{field.path}</span>
                            {typeof field.oldValue === 'string' && field.oldValue.length < 30 && (
                              <span className="text-slate-400 ml-1">({field.oldValue})</span>
                            )}
                          </span>
                        </li>
                      ))}
                      {fieldsToLose.length > 10 && (
                        <li className="text-slate-500 italic">... et {fieldsToLose.length - 10} autre(s)</li>
                      )}
                    </ul>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-3">
                    Cette action est <strong>irréversible</strong>. Voulez-vous continuer ?
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCancelChange}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition text-sm"
                >
                  <i className="bi bi-x-lg mr-1"></i>
                  Annuler
                </button>
                <button
                  onClick={handleConfirmChange}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-semibold transition text-sm"
                >
                  <i className="bi bi-check-lg mr-1"></i>
                  Confirmer le changement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TaxonomySelector;
